import { MaterialIcons } from '@expo/vector-icons';
import { Picker } from '@react-native-picker/picker';
import { useFeatureGate } from '@statsig/expo-bindings';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Dimensions, Platform, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Colors from '../../constants/Colors';
import Languages from '../../constants/Languages';
import Layout from '../../constants/Layout';
import MaterialSwitch from '../../utils/switch';
import NavBar from '../NavBar.jsx';
import DeleteAccount from './DeleteAccount';
import PushNotificationRegistration from './PushNotificationRegistration';
import { releaseId } from './releaseId';
import { useExpoModules } from '../../providers/ExpoModulesProvider';

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    flex: 1,
    alignItems: 'center',
  },
  scrollContainer: {
    flex: 1,
    width: '100%',
  },
  scrollContent: {
    alignItems: 'center',
    paddingBottom: 40,
  },
  mainContent: {
    flex: 1,
    maxWidth: 720,
    width: '100%',
  },
  infoCard: {
    padding: 16,
    borderBottomColor: Colors.grayBorder,
    borderBottomWidth: 1,
    backgroundColor: Colors.white,
  },
  subheader: {
    paddingHorizontal: 16,
    paddingTop: 24,
    paddingBottom: 12,
    backgroundColor: Colors.lightBackground,
  },
  subheaderText: {
    fontSize: 14,
    color: Colors.mediumBlue,
    fontWeight: 'bold',
  },
  label: {
    color: Colors.grayText,
    fontSize: 12,
  },
  dropdownIcon: {
    color: Colors.lightGray,
  },
  dropdownPlaceholder: {
    color: Colors.grayText,
    fontFamily: 'roboto-regular',
  },
  dropdown: {
    width: Dimensions.get('window').width - 32,
    maxWidth: 688,
    borderColor: Colors.white,
    backgroundColor: Colors.white,
  },
  cardView: {
    marginHorizontal: 0,
    marginVertical: 0.5,
    backgroundColor: Colors.lightBackground,
    paddingVertical: 20,
    elevation: 2,
  },
  descriptionText: {
    paddingTop: 36,
    paddingBottom: 16,
    paddingLeft: 8,
    fontSize: 20,
    color: '#808080',
    fontWeight: '500',
  },
  headerTitle: {
    fontSize: 20,
    color: Colors.mediumBlue,
    fontWeight: 'bold',
  },
  textStyle: {
    fontSize: 20,
    color: Colors.veryDarkGray,
  },
  verifyText: {
    fontSize: 18,
    color: Colors.profileVerified,
  },
  desSec: {
    padding: 10,
  },
  viewWithRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  viewWithXtaPadding: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 36,
  },
  dashedUnderline: {
    borderWidth: 1,
    borderColor: Colors.grayBorder,
    borderStyle: 'dashed',
    borderRadius: 1,
    width: '80%',
    marginBottom: 16,
  },
  detailText: {
    fontSize: 16,
    lineHeight: 24,
    color: Colors.veryDarkGray,
    maxWidth: Layout.fullWidth - 100,
  },
  activeText: {
    color: Colors.veryDarkGray,
  },
  labelTextStyle: {
    position: 'absolute',
    left: '100%',
  },
  halfWidth: {
    width: '50%',
  },
  saveChangesContainer: {
    position: 'absolute',
    backgroundColor: Colors.mediumBlue,
    height: 56,
    top: 0,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cancelSaveChanges: {
    paddingLeft: 16,
  },
  saveChangesText: {
    color: Colors.white,
    textTransform: 'uppercase',
    fontWeight: 'bold',
    marginRight: 16,
  },
  deleteAccountContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 16,
  },
  deleteAccountText: {
    color: Colors.brightRed,
    fontSize: 16,
  },
  releaseContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingBottom: 20,
  },
  releaseText: {
    color: Colors.lightGray,
  },
  helperTextContainer: {
    marginTop: 16,
  },
  helperText: {
    fontSize: 14,
    color: Colors.grayText,
    marginTop: 8,
    fontStyle: 'italic',
  },
  settingHelper: {
    fontSize: 12,
    color: Colors.grayText,
    marginTop: 4,
  },
  settingRow: {
    marginTop: 16,
  },
  sectionDescription: {
    fontSize: 14,
    color: Colors.grayText,
    marginBottom: 12,
  },
});

function UserSettings({ translation, actions, profile, navigation, registration }) {
  const { Updates, Constants } = useExpoModules();
  const { value: showMessageMethodGate } = useFeatureGate('neighbor_message_method_settings_visibility');

  const [state, setState] = useState({
    errors: {},
    user: { ...profile },
    showChangeLanguage: false,
    showSaveChanges: false,
    pushNotificationRegistration: false,
    switchesLoaded: false,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setState((prev) => ({ ...prev, switchesLoaded: true, showSaveChanges: false }));
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const { user: newUser } = registration;
    if (state.user.id !== newUser.id) {
      setState((prev) => ({ ...prev, user: { ...newUser } }));
    }
  }, [registration, state.user.id]);

  const somethingChanged = useCallback(
    (user) => {
      const updates = {};
      Object.keys(profile).forEach((key) => {
        if (profile[key] !== user[key]) updates[key] = user[key];
      });
      return { changes: updates, changed: !!Object.keys(updates).length };
    },
    [profile]
  );

  const onChangeLanguage = (value) => {
    const user = { ...state.user, language: value };
    const updates = somethingChanged(user);
    setState((prev) => ({
      ...prev,
      user,
      showChangeLanguage: false,
      showSaveChanges: updates.changed,
    }));
  };

  const setChanges = (field, value) => {
    const user = { ...state.user };
    if (field === 'email_unsubscribed' || field === 'receive_notifications') {
      user[field] = !!value;
    } else {
      user[field] = value;
    }
    const updates = somethingChanged(user);
    setState((prev) => ({ ...prev, user, showSaveChanges: updates.changed }));
  };

  const setNotifications = () => {
    setChanges('receive_notifications', 1);
    setState((prev) => ({ ...prev, pushNotificationRegistration: true }));
  };

  const removeNotifications = () => {
    setChanges('receive_notifications', 0);
  };

  const subscribeToEmail = () => {
    setChanges('email_unsubscribed', 0);
  };

  const unSubscribeToEmail = () => {
    setChanges('email_unsubscribed', 1);
  };

  const cancelShowSaveChanges = () => {
    setState((prev) => ({ ...prev, showSaveChanges: false }));
  };

  const confirmSaveChanges = () => {
    setState((prev) => ({ ...prev, showSaveChanges: false }));
    const updates = somethingChanged(state.user);
    if (updates.changed) {
      actions.updateUser(updates.changes);
    }
  };

  const capitalize = (string) => string.charAt(0).toUpperCase() + string.slice(1);

  const deleteAccount = () => {
    actions.deleteAccount(() => {
      actions.logout(() => {
        navigation.navigate('Language');
      });
    });
  };

  const openDeleteAccount = () => {
    actions.openDeleteAccount();
  };

  const closeDeleteAccount = () => {
    actions.closeDeleteAccount();
  };

  const { showSaveChanges, switchesLoaded, user, pushNotificationRegistration } = state;
  const { showDeleteAccount } = registration;

  const labels = {
    app_release_id: translation.app_release_id,
    receive_notifications: translation.fragment_profile_switch_receivenotifications,
    receive_notifications_helper: translation.receive_notifications_helper,
    language: translation.fragment_profile_textview_language,
    notifications: capitalize(translation.fragment_profile_textview_notifications),
    settings: capitalize(translation.settings),
    save_changes: translation.fragment_profile_button_savechanges,
    select_language: translation.fragment_registration_language_textview_title,
    email_subscribe: translation.email_subscribe,
    email_subscribe_helper: translation.email_subscribe_helper,
    delete_account: translation.delete_account,
    message_preferences: translation.message_preferences,
    message_preferences_description: translation.message_preferences_description,
  };

  // Determine which message preference options to show (memoized to prevent infinite loop)
  const messageMethodOptions = useMemo(() => {
    // Capability detection for message method options
    const hasSms = user.phone && user.phone !== '' && user.sms_opt_in === 1;
    const hasEmail = user.email && user.email !== '' && !user.email_unsubscribed;
    const hasPush = user.expo_push_token && user.expo_push_token !== '';

    const options = [];
    // Option 0: Email & App Notifications (push/email with SMS fallback) - needs push OR email
    if (hasPush || hasEmail) {
      options.push({ value: 0, label: translation.message_method_option_0 });
    }
    // Option 1: Prefer SMS (with push/email fallback) - needs SMS
    if (hasSms) {
      options.push({ value: 1, label: translation.message_method_option_1 });
    }
    return options;
  }, [user.phone, user.sms_opt_in, user.email, user.email_unsubscribed, user.expo_push_token, translation]);
  const showMessageMethod = messageMethodOptions.length > 0;

  const onChangeMessageMethod = (value) => {
    setChanges('message_method', Number(value));
  };

  // Helper text for missing capabilities
  const getMessageMethodHelperText = () => {
    const helpers = [];
    // SMS helper
    if (!user.phone || user.phone === '') {
      helpers.push(translation.message_method_helper_add_phone);
    } else if (user.sms_opt_in !== 1) {
      helpers.push(translation.message_method_helper_sms_opt_in);
    }
    // Email helper
    if (!user.email || user.email === '') {
      helpers.push(translation.message_method_helper_add_email);
    } else if (user.email_unsubscribed) {
      helpers.push(translation.message_method_helper_email_unsub);
    }
    // Push helper - context aware based on platform
    const hasPush = user.expo_push_token && user.expo_push_token !== '';
    if (!hasPush) {
      if (Platform.OS === 'web') {
        helpers.push(translation.message_method_helper_add_push);
      } else {
        helpers.push(translation.message_method_helper_enable_push);
      }
    }
    return helpers;
  };

  const messageMethodHelperTexts = getMessageMethodHelperText();

  // Auto-select first valid option if current message_method is not available
  useEffect(() => {
    if (showMessageMethod && messageMethodOptions.length > 0) {
      const currentValueValid = messageMethodOptions.some((opt) => opt.value === user.message_method);
      // Guard: only update if current value is invalid AND we're not already set to the first option
      if (!currentValueValid && user.message_method !== messageMethodOptions[0].value) {
        const newValue = messageMethodOptions[0].value;
        setState((prev) => {
          const updatedUser = { ...prev.user, message_method: newValue };
          const updates = somethingChanged(updatedUser);
          return { ...prev, user: updatedUser, showSaveChanges: updates.changed };
        });
      }
    }
  }, [messageMethodOptions, user.message_method, showMessageMethod, somethingChanged]);

  const { languages } = Languages;
  let userLanguage;
  languages.forEach((lang) => {
    if (lang.full === user.language) userLanguage = lang.full;
  });

  let pushComponent;
  if (pushNotificationRegistration) {
    pushComponent = <PushNotificationRegistration user={user} actions={actions} translation={translation} />;
  }

  const dropdownValues = [];
  Languages.languages.forEach((lang, index) => {
    dropdownValues.push(<Picker.Item key={`Language-${index}`} label={lang.label} value={lang.full} />);
  });

  let emailSubscribed = true;
  let showEmailSubscribed = true;
  if (user.email_unsubscribed) emailSubscribed = false;
  if (!user.email || user.email === '') showEmailSubscribed = false;

  let deleteAccountModal = null;
  if (showDeleteAccount) {
    deleteAccountModal = (
      <DeleteAccount onOk={deleteAccount} onCancel={closeDeleteAccount} translation={translation} />
    );
  }

  return (
    <View style={styles.container}>
      <NavBar title={labels.settings} onPressBack={() => navigation.navigate('landing')} />

      {showSaveChanges && switchesLoaded ? (
        <View style={styles.saveChangesContainer}>
          <TouchableOpacity onPress={cancelShowSaveChanges} style={styles.cancelSaveChanges}>
            <MaterialIcons name={'close'} size={24} color={Colors.white} />
          </TouchableOpacity>
          <TouchableOpacity onPress={confirmSaveChanges} style={styles.cancelSaveChanges}>
            <Text style={styles.saveChangesText}>{labels.save_changes}</Text>
          </TouchableOpacity>
        </View>
      ) : null}

      <ScrollView style={styles.scrollContainer} contentContainerStyle={styles.scrollContent}>
        <View style={styles.mainContent}>
          <View style={styles.subheader}>
            <Text style={styles.subheaderText}>{labels.language}</Text>
          </View>
          <View style={styles.infoCard}>
            <View style={styles.row}>
              <Picker
                mode="dropdown"
                selectedValue={userLanguage}
                placeholder={labels.language}
                onValueChange={onChangeLanguage}
                style={styles.dropdown}
              >
                {dropdownValues}
              </Picker>
            </View>
          </View>

          <View style={styles.subheader}>
            <Text style={styles.subheaderText}>{labels.notifications}</Text>
          </View>
          <View style={styles.infoCard}>
            <View>
              <View style={styles.viewWithRow}>
                <Text style={styles.detailText}>{labels.receive_notifications}</Text>
                <View>
                  <MaterialSwitch
                    onActivate={setNotifications}
                    onDeactivate={removeNotifications}
                    active={Boolean(user.receive_notifications)}
                  />
                </View>
              </View>
              <Text style={styles.settingHelper}>{labels.receive_notifications_helper}</Text>
            </View>

            {showEmailSubscribed ? (
              <View style={styles.settingRow}>
                <View style={styles.viewWithRow}>
                  <Text style={styles.detailText}>{labels.email_subscribe}</Text>
                  <View>
                    <MaterialSwitch
                      onActivate={subscribeToEmail}
                      onDeactivate={unSubscribeToEmail}
                      active={emailSubscribed}
                    />
                  </View>
                </View>
                <Text style={styles.settingHelper}>{labels.email_subscribe_helper}</Text>
              </View>
            ) : null}
          </View>

          {showMessageMethod && showMessageMethodGate ? (
            <>
              <View style={styles.subheader}>
                <Text style={styles.subheaderText}>{labels.message_preferences}</Text>
              </View>
              <View style={styles.infoCard}>
                <Text style={styles.sectionDescription}>{labels.message_preferences_description}</Text>
                <View style={styles.row}>
                  <Picker
                    mode="dropdown"
                    selectedValue={user.message_method}
                    onValueChange={onChangeMessageMethod}
                    style={styles.dropdown}
                  >
                    {messageMethodOptions.map((option) => (
                      <Picker.Item
                        key={`MessageMethod-${option.value}`}
                        label={option.label}
                        value={option.value}
                      />
                    ))}
                  </Picker>
                </View>
                {messageMethodHelperTexts.length > 0 && (
                  <View style={styles.helperTextContainer}>
                    {messageMethodHelperTexts.map((helperText, index) => (
                      <Text key={`helper-${index}`} style={styles.helperText}>
                        {helperText}
                      </Text>
                    ))}
                  </View>
                )}
              </View>
            </>
          ) : null}

          <View style={styles.deleteAccountContainer}>
            <TouchableOpacity onPress={openDeleteAccount}>
              <Text style={styles.deleteAccountText}>{labels.delete_account}</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.releaseContainer}>
            <Text style={styles.releaseText}>{labels.app_release_id}</Text>
            <Text style={styles.releaseText}>{releaseId(Updates, Constants)}</Text>
          </View>
        </View>

        {pushComponent}
      </ScrollView>
      {deleteAccountModal}
    </View>
  );
}

UserSettings.propTypes = {
  translation: PropTypes.objectOf(PropTypes.any).isRequired,
  actions: PropTypes.objectOf(PropTypes.any).isRequired,
  profile: PropTypes.objectOf(PropTypes.any).isRequired,
  navigation: PropTypes.objectOf(PropTypes.any).isRequired,
  registration: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default UserSettings;
